import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import useDimensions from "react-cool-dimensions";
import { UXPContext } from "@core/context/Context";
const classNames = require('classnames');
/**
 *
 * @export
 *
 * This component is used to create a radial gauge.
 *
 * ## Demo
 * Find a [Demo](https://lucy-uxp.github.io/dev/showcase.html#radial-gauge) here
 *
 *
 * @example
 * ```
 *  <RadialGauge
 *      value={10}
 *      min={0}
 *      max={100}
 *  />
 * ```
 *
 * @example
 * ```
 *  <RadialGauge
 *      value={10}
 *      min={0}
 *      max={100}
 *      label={() => <>Equipment Heat</>}
 *      legend={true}
 *      gradient={true}
 *      thickness={20}
 *      largeTick={5}
 *      smallTick={2}
 *      colors={[
 *          {color: 'cyan', stopAt: 12.5},
 *          {color: 'green', stopAt: 70},
 *          {color: 'orange', stopAt: 87.5},
 *          {color: 'red', stopAt: 100},
 *      ]}
 *
 *  />
 * ```
 */
const RadialGauge = (props) => {
    var _a, _b, _c, _d;
    const context = React.useContext(UXPContext);
    // generate a random id
    let _id = new Date().getTime().toString() + (Math.round(Math.random() * 100000)).toString();
    // get width & height
    const { width, height, entry, unobserve, observe } = useDimensions({
        onResize: (p) => { },
    });
    // defaults 
    let HWRatio = 0.75;
    let minSvgWidth = 150;
    let svgWidth = width > minSvgWidth ? width : minSvgWidth;
    let svgHeight = svgWidth * HWRatio;
    // radius & stroke
    let radius = svgWidth / 3;
    let __strokeWidth = radius * 0.11;
    let strokeWidth = __strokeWidth;
    if (props.thickness) {
        if (!(props.thickness > radius * 0.25)) {
            strokeWidth = props.thickness;
        }
        else {
            strokeWidth = radius * 0.25;
        }
    }
    // get rotation of needle for the given value
    let min = props.min || 0.0;
    let max = props.max || 1.0;
    let value = Number(props.value);
    value = Math.min(Math.max(value, min), max);
    let percentage = (value - min) / (max - min);
    let rotateNeedle = (240 * percentage - 120);
    // calc marker values
    let markers = [];
    for (let i = 0; i < 9; i++) {
        let gap = (max - min) / 8;
        markers.push(Math.round((((i * gap) + min) + Number.EPSILON) * 100) / 100);
    }
    let colors = props.colors;
    if (!colors || colors.length == 0) {
        // Use theme chart colors for defaults
        colors = [
            { color: ((_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.chartColor1) || '#12B76A', stopAt: (0.25 * (max - min)) + min },
            { color: ((_b = context === null || context === void 0 ? void 0 : context.theme) === null || _b === void 0 ? void 0 : _b.chartColor2) || '#3C2EE2', stopAt: (0.5 * (max - min)) + min },
            { color: ((_c = context === null || context === void 0 ? void 0 : context.theme) === null || _c === void 0 ? void 0 : _c.chartColor4) || '#F79009', stopAt: (0.75 * (max - min)) + min },
            { color: ((_d = context === null || context === void 0 ? void 0 : context.theme) === null || _d === void 0 ? void 0 : _d.chartColor3) || '#D92D20', stopAt: max }
        ];
    }
    colors = colors.sort((a, b) => a.stopAt - b.stopAt);
    let circumference = 2 / 3 * (2 * Math.PI) * (radius + strokeWidth);
    let largeTick = 4;
    if (props.largeTick) {
        largeTick = props.largeTick > 6 ? 6 : props.largeTick;
    }
    let smallTick = 1;
    if (props.smallTick) {
        smallTick = props.smallTick > 3 ? 3 : props.smallTick;
    }
    let tickColor = props.tickColor && props.tickColor.trim().length > 0
        ? props.tickColor
        : 'var(--primaryBGColor)';
    let fontSize = radius * 0.1;
    // helper functions to calculate path of the arc
    function describeArc(x, y, radius, spread, startAngle, endAngle, largeArc, fill) {
        var innerStart = polarToCartesian(x, y, radius, endAngle);
        var innerEnd = polarToCartesian(x, y, radius, startAngle);
        var outerStart = polarToCartesian(x, y, radius + spread, endAngle);
        var outerEnd = polarToCartesian(x, y, radius + spread, startAngle);
        var largeArcFlag = largeArc ? "1" : "0";
        var d = [
            "M",
            outerStart.x,
            outerStart.y,
            "A",
            radius + spread,
            radius + spread,
            0,
            largeArcFlag,
            0,
            outerEnd.x,
            outerEnd.y
        ];
        if (fill) {
            d = [...d, ...[
                    "L",
                    innerEnd.x,
                    innerEnd.y,
                    "A",
                    radius,
                    radius,
                    0,
                    largeArcFlag,
                    1,
                    innerStart.x,
                    innerStart.y,
                    "L",
                    outerStart.x,
                    outerStart.y,
                    "Z"
                ]];
        }
        // return { path: d, coords: { start: outerStart, end: outerEnd } };
        return d.join(" ");
    }
    function polarToCartesian(centerX, centerY, radius, angleInDegrees) {
        var angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
        return {
            x: centerX + radius * Math.cos(angleInRadians),
            y: centerY + radius * Math.sin(angleInRadians)
        };
    }
    function toRadian(deg) {
        return deg * (Math.PI / 180);
    }
    function aSinX(a, x) {
        return a * Math.sin(toRadian(x));
    }
    function aCosX(a, x) {
        return a * Math.cos(toRadian(x));
    }
    function renderGaugeBg() {
        const centerX = svgWidth / 2;
        const centerY = svgWidth / 2;
        const startAngle = -120; // Start at -120 degrees
        const totalAngle = 240; // Total sweep of 240 degrees
        return _jsx("div", { className: "gauge-bg", style: { width: `${svgWidth}px`, height: `${svgWidth}px` }, children: _jsxs("svg", { width: svgWidth, height: svgWidth, children: [_jsx("defs", { children: props.gradient && colors.map((color, index) => (_jsxs("linearGradient", { id: `gradient-${_id}-${index}`, x1: "0%", y1: "0%", x2: "100%", y2: "0%", children: [_jsx("stop", { offset: "0%", stopColor: index > 0 ? colors[index - 1].color : color.color }), _jsx("stop", { offset: "100%", stopColor: color.color })] }, index))) }), colors.map((color, index) => {
                        const prevStopValue = index > 0 ? colors[index - 1].stopAt : min;
                        const currentStopValue = color.stopAt;
                        // Clamp stop values to gauge min/max range
                        const clampedPrevStop = Math.max(min, Math.min(max, prevStopValue));
                        const clampedCurrentStop = Math.max(min, Math.min(max, currentStopValue));
                        // Skip segments that are completely outside the gauge range
                        if (clampedPrevStop >= max || clampedCurrentStop <= min || clampedPrevStop === clampedCurrentStop) {
                            return null;
                        }
                        // Calculate angles for this segment using clamped values
                        const segmentStartPercentage = (clampedPrevStop - min) / (max - min);
                        const segmentEndPercentage = (clampedCurrentStop - min) / (max - min);
                        const segmentStartAngle = startAngle + (segmentStartPercentage * totalAngle);
                        const segmentEndAngle = startAngle + (segmentEndPercentage * totalAngle);
                        // Calculate the arc span correctly
                        const arcSpan = Math.abs(segmentEndAngle - segmentStartAngle);
                        const isLargeArc = arcSpan > 180;
                        // Create arc path for this color segment
                        const arcPath = describeArc(centerX, centerY, radius, strokeWidth, segmentStartAngle, segmentEndAngle, isLargeArc, true);
                        const fillColor = props.gradient ? `url(#gradient-${_id}-${index})` : color.color;
                        return (_jsx("path", { d: arcPath, fill: fillColor, stroke: "none" }, index));
                    })] }) });
    }
    function renderTicks() {
        const startAngle = -120; // Start angle of the gauge arc
        const totalAngle = 240; // Total sweep of the gauge
        // Position ticks at the outer edge of the arc and make them fill the entire arc thickness
        const tickStartRadius = radius + strokeWidth; // Outer edge of the arc
        const tickLength = strokeWidth; // Full arc thickness
        // Calculate small tick positions (3 between each pair of large ticks)
        const smallTickPositions = [];
        for (let i = 0; i < markers.length - 1; i++) {
            const currentMarker = markers[i];
            const nextMarker = markers[i + 1];
            const gap = (nextMarker - currentMarker) / 4; // Divide into 4 parts for 3 small ticks
            for (let j = 1; j <= 3; j++) {
                smallTickPositions.push(currentMarker + (gap * j));
            }
        }
        return _jsxs(_Fragment, { children: [_jsx("div", { className: "tick-container", style: { width: `${svgWidth}px`, height: `${svgWidth}px` }, children: smallTickPositions.map((tickValue, i) => {
                        const percentage = (tickValue - min) / (max - min);
                        const tickAngle = startAngle + (percentage * totalAngle);
                        return (_jsx("div", { className: "tick", style: {
                                backgroundColor: `${tickColor}`,
                                width: `${smallTick}px`,
                                height: `${tickLength}px`, // Full arc thickness
                                position: 'absolute',
                                top: `${(svgWidth / 2) - tickStartRadius}px`, // Start from outer edge
                                left: `${(svgWidth / 2) - (smallTick / 2)}px`,
                                transformOrigin: `center ${tickStartRadius}px`, // Set origin to outer edge
                                transform: `rotate(${tickAngle}deg)`
                            } }, i));
                    }) }), _jsx("div", { className: "tick-container", style: { width: `${svgWidth}px`, height: `${svgWidth}px` }, children: markers.map((markerValue, i) => {
                        const percentage = (markerValue - min) / (max - min);
                        const tickAngle = startAngle + (percentage * totalAngle);
                        return (_jsx("div", { className: "tick", style: {
                                backgroundColor: `${tickColor}`,
                                width: `${largeTick}px`,
                                height: `${tickLength}px`, // Full arc thickness
                                position: 'absolute',
                                top: `${(svgWidth / 2) - tickStartRadius}px`, // Start from outer edge
                                left: `${(svgWidth / 2) - (largeTick / 2)}px`,
                                transformOrigin: `center ${tickStartRadius}px`, // Set origin to outer edge
                                transform: `rotate(${tickAngle}deg)`
                            } }, i));
                    }) })] });
    }
    function renderNumbers() {
        let _radius = radius * 0.9;
        function getStyles(n, i) {
            let _percentage = (n - min) / (max - min);
            let _ang = 240 * _percentage - 120;
            let _absAng = Math.abs(_ang);
            let styles = {};
            if (_absAng == 0) {
                styles = {
                    top: `0px`,
                    left: `${_radius}px`,
                    transform: `translateX(-50%)`
                };
            }
            else if (_absAng == 90) {
                if (_ang > 0) {
                    styles = {
                        top: `${_radius}px`,
                        right: `0px`,
                        transform: `translateY(-50%)`
                    };
                }
                else {
                    styles = {
                        top: `${_radius}px`,
                        left: `0px`,
                        transform: `translateY(-50%)`
                    };
                }
            }
            else if (_absAng > 90) {
                if (_ang > 0) {
                    styles = {
                        bottom: `${(_radius - aSinX(_radius, Math.abs(_absAng - 90)))}px`,
                        right: `${(_radius - aCosX(_radius, Math.abs(_absAng - 90)))}px`,
                        // transform: `translate(${-fontSize}px, ${-fontSize}px)`
                    };
                }
                else {
                    styles = {
                        bottom: `${(_radius - aSinX(_radius, Math.abs(_absAng - 90)))}px`,
                        left: `${(_radius - aCosX(_radius, Math.abs(_absAng - 90)))}px`,
                        // transform: `translate(${fontSize}px, ${fontSize}px)`
                    };
                }
            }
            else {
                if (_ang > 0) {
                    styles = {
                        top: `${(_radius - aSinX(_radius, Math.abs(_absAng - 90)))}px`,
                        right: `${(_radius - aCosX(_radius, Math.abs(_absAng - 90)))}px`,
                    };
                }
                else {
                    styles = {
                        top: `${(_radius - aSinX(_radius, Math.abs(_absAng - 90)))}px`,
                        left: `${(_radius - aCosX(_radius, Math.abs(_absAng - 90)))}px`,
                    };
                }
            }
            return styles;
        }
        return _jsx("div", { className: "gauge-number-cont", style: {
                width: `${2 * _radius}px`,
                height: `${2 * _radius}px`,
                top: `${(svgWidth / 2) - (_radius)}px`,
                left: `${(svgWidth / 2) - (_radius)}px`,
                borderRadius: '50%',
                fontSize: `${fontSize}px`
            }, children: _jsx("div", { className: "number-cont", children: markers.map((n, i) => {
                    return _jsx("div", { className: "number", style: getStyles(n, i), children: n }, i);
                }) }) });
    }
    function renderNeedle() {
        var _a;
        // needle styles 
        let needleHeight = radius * 0.65;
        let needleWidth = __strokeWidth * 1.5;
        let _styles = {
            width: `${needleWidth}px`,
            height: `${needleHeight}px`,
            transformOrigin: 'bottom',
            left: `${(svgWidth / 2) - (needleWidth / 2)}px`,
            top: `${(svgWidth / 2) - (needleHeight)}px`,
            transform: `rotate(${rotateNeedle}deg)`
        };
        let needleStyles = {
            '--needleWidth': `${(needleWidth)}px`,
            '--needleHeight': `${(needleHeight)}px`,
            '--smallEdgeWidth': `${(needleWidth * 0.2)}px`,
            '--largeEdgeWidth': `${(needleWidth)}px`,
            '--needleColor': props.needleColor || ((_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.secondaryTextColor) || '#667085'
        };
        return _jsx("div", { className: "needle-cont", style: _styles, children: _jsx("div", { className: "needle", style: needleStyles, children: _jsx("div", { className: "circle" }) }) });
    }
    function renderLabel() {
        return _jsxs(_Fragment, { children: [_jsx("div", { className: "gauge-cont-spacer" }), _jsx("div", { className: "gauge-label-cont", children: _jsx("div", { className: "label", style: {
                            width: `${radius * 2}px`,
                            height: 'auto',
                            fontSize: `${__strokeWidth * 1.1}px`
                        }, children: props.label() }) })] });
    }
    // render legend 
    function renderLegend() {
        var _a;
        let prevStop = 0;
        let _width = 1.8 * (radius);
        let breakPoints = [];
        let colorBreakpoints = [];
        colors.map((c, k) => {
            breakPoints.push(c.stopAt);
            let _percentage = (c.stopAt - min) / (max - min);
            colorBreakpoints.push(`${c.color} ${_percentage * 100}%`);
        });
        let styles = {
            width: `${_width}px`,
            fontSize: `${fontSize}px`,
            '--lableColor': props.labelColor || ((_a = context === null || context === void 0 ? void 0 : context.theme) === null || _a === void 0 ? void 0 : _a.primaryTextColor) || '#101828'
        };
        if (props.gradient) {
            styles.backgroundImage = `linear-gradient(to right, ${colorBreakpoints.join(",").replace(/\"/g, " ")})  `;
        }
        return _jsxs(_Fragment, { children: [_jsx("div", { className: "gauge-cont-spacer" }), _jsx("div", { className: classNames("gauge-legend-cont"), children: _jsx("div", { className: 'colors-cont', style: styles, children: colors.map((c, k) => {
                            let _percentage = (c.stopAt - min) / (max - min);
                            let legendWidth = _width * (_percentage - prevStop);
                            let backgroundColor = props.gradient ? 'transparent' : c.color;
                            prevStop = _percentage;
                            return _jsxs("div", { className: "tile", style: {
                                    backgroundColor: backgroundColor,
                                    width: `${legendWidth}px`
                                }, children: [k == 0 && _jsx("div", { className: 'label', children: min }), _jsx("div", { className: "label", children: c.stopAt })] }, k);
                        }) }) })] });
    }
    let styles = Object.assign({}, props.styles);
    styles.minWidth = `${minSvgWidth}px`;
    styles.backgroundColor = props.backgroundColor || 'var(--primaryBGColor)';
    styles.color = props.labelColor || 'var(--primaryTextColor)';
    //render
    return (_jsxs("div", { className: classNames("uxpcore_radialgauge", props.className), ref: observe, style: styles, children: [_jsxs("div", { className: "gauge-face-container", style: {
                    width: `${svgWidth}px`,
                    height: `${svgHeight}px`
                }, children: [_jsxs("div", { className: "gauge-face", style: {
                            width: `${svgWidth - 2}px`,
                            height: `${svgHeight - 2}px`
                        }, children: [renderGaugeBg(), renderTicks()] }), renderNumbers(), renderNeedle()] }), props.label && renderLabel(), props.legend && renderLegend()] }));
};
export default RadialGauge;
